/** Field Operator's Manual — in-app help.
 *
 * Static reference content for Vince. Designed to be skimmable on iPad in the field.
 * Available at /help. Linked from Settings and the AppShell header (? icon).
 */
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/Common';
import {
  CompassIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ShieldCheckIcon,
  CaretLeftIcon,
} from '@phosphor-icons/react';

export default function HelpPage() {
  return (
    <>
      <PageHeader
        eyebrow="Field Operator&rsquo;s Manual"
        title="Help &amp; Reference"
        subtitle="The full how-to for every tab, bucket, status, and hazard flag. Skim before a walkthrough &mdash; re-read between rooms."
      />

      <div className="px-5 md:px-8 pb-12 max-w-5xl">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold mb-4"
          style={{ color: 'var(--gl-navy)', fontFamily: 'Inter', letterSpacing: '0.1em' }}
          data-testid="help-back-to-settings"
        >
          <CaretLeftIcon size={16} weight="bold" /> Back to Settings
        </Link>

        {/* Quick-jump TOC */}
        <div
          className="gl-card mb-6"
          style={{ borderLeft: '4px solid var(--gl-gold)', background: '#fdf6e3' }}
          data-testid="help-toc"
        >
          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
            <CompassIcon size={22} weight="fill" color="var(--gl-navy)" />
            <div className="font-display font-extrabold uppercase text-lg" style={{ color: 'var(--gl-dark)' }}>
              Jump To
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            {[
              ['tabs', 'A · The 7 Tabs'],
              ['buckets', 'B · The 6 Buckets'],
              ['states', 'C · The Status States'],
              ['legacy', 'D · The 9 Element-Checklist Docs'],
              ['flags', 'E · The 6 Hazard Flags'],
              ['workflow', '🎯 Engagement Workflow'],
            ].map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="px-3 py-1.5 border-2 rounded-sm font-bold uppercase tracking-wider text-xs"
                style={{ borderColor: 'var(--gl-navy)', color: 'var(--gl-navy)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
              >
                {label}
              </a>
            ))}
          </div>
        </div>

        {/* SECTION A — TABS */}
        <Section id="tabs" eyebrow="Section A" title="The 7 Tabs Inside Every Engagement">
          <p className="mb-3" style={{ color: 'var(--gl-muted)' }}>
            An engagement is <b>one facility visit</b>. The tabs are your workflow, in order:
          </p>
          <Table headers={['#', 'Tab', 'What it does', 'When to use it']}>
            <Row n="1" tab="Setup" what="Facility info + hazard profile. Drives everything downstream." when="First. Get hazard flags right or the rest is wrong." />
            <Row n="2" tab="Findings" what="Your Gemba walk — hazards observed, photos, CFR citations, P1/P2/P3." when="Walkthrough or Combined engagements only." />
            <Row n="3" tab="Docs ⭐" what="Rule-driven master document list — which docs THIS facility needs." when="First thing after Setup. Walk the client through this on screen." />
            <Row n="4" tab="Checklist" what="Legacy 7-category gap check (53 items)." when="Being deprecated. Skip on new engagements — Docs replaces it." />
            <Row n="5" tab="Doc Review" what="CFR-element deep dive for the 9 written programs. 'Open Detailed Review' lives here." when="After Docs. Grade an IIPP clause-by-clause." />
            <Row n="6" tab="Media" what="Every photo / file attached to this engagement." when="Verify offline photos synced before report." />
            <Row n="7" tab="Report" what="Generates the PDF + the client share link." when="Last. After all findings and doc statuses are set." />
          </Table>
        </Section>

        {/* SECTION B — BUCKETS */}
        <Section id="buckets" eyebrow="Section B" title="The 6 Buckets in the Docs Tab">
          <Table headers={['#', 'Bucket', "What's inside", 'When shown']}>
            <Row n="1" tab="Core Written Programs" what="IIPP · HazCom · SDS · Container Labels · PPE · LOTO · Machine Guarding · Forklift · EAP · Hearing/Respirator/Confined Space" when="Always." />
            <Row n="2" tab="Injury & Illness Records" what="OSHA 300 · 300A · 301 · Fatality Reporting Procedure" when="Always (some exempt at ≤10 emp)." />
            <Row n="3" tab="Training Records" what="Sign-offs proving each training actually happened" when="Always." />
            <Row n="4" tab="Inspection & Maintenance Records" what="Facility inspections · LOTO annual · Forklift pre-shift · Equipment maintenance · Corrective Action Log" when="Always." />
            <Row n="5" tab="Regulated Substances" what="Lead · Silica · Hex Chrome exposure control plans" when={<>Only when <code>chemical_exposure</code> flag is on.</>} />
            <Row n="6" tab="FMCSA / Commercial Motor Vehicle" what="DQF · HOS/ELD · DVIR · DOT inspections · Vehicle maintenance · MCS-90" when={<>Only when <code>has_fleet</code> flag is on.</>} />
          </Table>
          <Callout>
            <b>Rule:</b> a bucket collapses into a slim &ldquo;Not applicable to this facility&rdquo; peek at the bottom if every doc in it doesn&rsquo;t apply. One tap reveals it &mdash; useful if you realize mid-walkthrough a flag should have been on.
          </Callout>
        </Section>

        {/* SECTION C — STATES */}
        <Section id="states" eyebrow="Section C" title="The Status States">
          <p className="mb-3" style={{ color: 'var(--gl-muted)' }}>
            Two kinds of state — <b>computed</b> (the rule engine decides) and <b>user override</b> (you set it on the chip).
          </p>

          <SubH>Computed (badge shows automatically)</SubH>
          <Table headers={['Badge', 'What it means']}>
            <Row n={<Badge color="var(--gl-navy)" bg="#eff4fb">Required</Badge>} tab="" what="Facility MUST have this doc. No exemption." />
            <Row n={<Badge color="var(--gl-p2)" bg="#fef4ea">Conditional</Badge>} tab="" what="Would be required IF a hazard flag was on. Hidden by default." />
            <Row n={<Badge color="var(--gl-muted)" bg="#f3f4f6">Exempt</Badge>} tab="" what="Exempt by CFR (e.g., 300 logs at ≤10 employees). CFR cite shown below the doc." />
          </Table>

          <SubH>User Override (chip you tap)</SubH>
          <Table headers={['Chip', 'When to use it']}>
            <Row n={<ChipPreview Icon={CheckCircleIcon} color="var(--gl-p3)" label="Present" />} tab="" what="You reviewed it. Exists, current, compliant." />
            <Row n={<ChipPreview Icon={XCircleIcon} color="var(--gl-p1)" label="Missing" />} tab="" what="Required but not on site, OR so out-of-date it doesn't count." />
            <Row n={<ChipPreview Icon={MinusCircleIcon} color="var(--gl-muted)" label="N/A" />} tab="" what="Doesn't apply to this facility (overrides any computed state)." />
          </Table>

          <Callout tone="warn">
            <b>Decision rule on Missing vs N/A:</b> if a doc would be a finding in a real OSHA inspection &rarr; mark <b>Missing</b>, not N/A. N/A means it genuinely doesn&rsquo;t apply.
          </Callout>
        </Section>

        {/* SECTION D — LEGACY DOCS */}
        <Section id="legacy" eyebrow="Section D" title="The 9 Docs with Element-Level Checklists">
          <p className="mb-3" style={{ color: 'var(--gl-muted)' }}>
            These are the only docs where <b>&ldquo;Open Detailed Review&rdquo;</b> opens a CFR-clause-by-clause checklist. Live on the <b>Doc Review</b> tab. The other 33 docs in the new Docs tab are status-only for now (Phase B will fill in their element checklists).
          </p>
          <Table headers={['#', 'Document', 'CFR', 'Critical to catch']}>
            <Row n="1" tab="IIPP" what="1910 General Duty" when="Hierarchy of controls clause" />
            <Row n="2" tab="EAP (Emergency Action Plan)" what="1910.38" when="Posted evacuation routes throughout facility" />
            <Row n="3" tab="LOTO Program" what="1910.147" when={<><b>Machine-specific procedures</b> — most common audit fail at small manufacturers</>} />
            <Row n="4" tab="HazCom Program" what="1910.1200" when="SDS for every chemical actually on site" />
            <Row n="5" tab="PPE Hazard Assessment" what="1910.132(d)" when="Written signed certification (not just a verbal walk-around)" />
            <Row n="6" tab="New Hire / Onboarding" what="Per applicable standard" when="Sign-offs by each new employee with date" />
            <Row n="7" tab="Hearing Conservation" what="1910.95" when="Audiometric baselines for each exposed worker" />
            <Row n="8" tab="SDS Library" what="1910.1200(g)" when="Accessible during ALL shifts (not locked in a manager's office)" />
            <Row n="9" tab="OSHA 300 Log" what="1904.29" when="300A posted Feb 1 – Apr 30; 5-year retention" />
          </Table>
        </Section>

        {/* SECTION E — HAZARD FLAGS */}
        <Section id="flags" eyebrow="Section E" title="The 6 Hazard Flags in Setup">
          <p className="mb-3" style={{ color: 'var(--gl-muted)' }}>
            Flip each ON if the condition exists at the facility. Each unlocks specific docs across all 4 main buckets.
          </p>
          <Table headers={['Flag', 'Turn ON when…', 'Docs it adds']}>
            <Row n="forklift_on_site" tab="Any PIT on site — forklift, reach truck, walkie operator" what="Forklift Program · Operator Certs · Pre-Shift Inspection · Maintenance · Operator Training" when="" />
            <Row n="equipment_with_moving_parts" tab="Any machine needing guarding or LOTO — presses, conveyors, mills, saws, mixers" what="LOTO Program · Machine-Specific Procedures · Machine Guarding Docs · Annual LOTO Inspection · Equipment Maintenance · LOTO Training · Machine Guarding Training" when="" />
            <Row n="noise_exposure" tab="8-hr TWA ≥ 85 dBA, or conversation-impossible noise zones" what="Hearing Conservation Program · Annual Hearing Training" when="" />
            <Row n="respirator_use" tab="Respirators required OR voluntarily used (even N95s for nuisance dust)" what="Respiratory Protection Program · Respirator Training" when="" />
            <Row n="confined_space" tab="Permit-required confined spaces — tanks, manholes, silos, large ducts" what="Confined Space Program (1910.146)" when="" />
            <Row n="chemical_exposure" tab="Lead / silica / hex chrome / other regulated substance ≥ action level" what="Lead Plan · Silica Plan · Hex Chrome Plan" when="" />
          </Table>
          <Callout>
            <b>Plus <code>has_fleet</code></b> in its own panel below — turns on the entire FMCSA bucket (DQF, HOS, DVIR, etc.).
          </Callout>
        </Section>

        {/* SECTION 🎯 — WORKFLOW */}
        <Section id="workflow" eyebrow="🎯 Full Workflow" title="The Order You Work an Engagement">
          <ol className="space-y-3 pl-1" style={{ counterReset: 'step' }}>
            {[
              { title: 'Setup tab', body: 'Fill facility info. Set industry_type, employee_count. Flip every hazard flag that applies. Save.' },
              { title: 'Docs tab', body: 'Walk client through the bucket list on screen. For each doc: Present / Missing / N/A. Use notes for "last revised 2022, expired" context.' },
              { title: 'Doc Review tab', body: 'For each Present doc with the gold "Element Checklist" badge, tap "Open Detailed Review" and grade each CFR element. This is where you catch the LOTO program that has no machine-specific procedures.' },
              { title: 'Findings tab', body: 'Walk the floor. Log each P1/P2/P3 with photo + CFR. AI-assisted CFR lookup is online-only.' },
              { title: 'Media tab', body: 'Verify offline photos synced once you\'re back on wifi.' },
              { title: 'Report tab', body: 'Generate PDF. Review the AI exec summary. Copy the public share link → send to client.' },
              { title: 'Phase 2 Scope Sheet', body: 'From Dashboard, generate the fixed-price quote PDF. Hand to client as your leave-behind.' },
            ].map((s, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span
                  className="font-display font-extrabold text-xl shrink-0 leading-none w-8 h-8 rounded-sm flex items-center justify-center"
                  style={{ background: 'var(--gl-navy)', color: '#fff' }}
                >
                  {i + 1}
                </span>
                <div>
                  <div className="font-bold uppercase tracking-wider text-sm" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.06em' }}>
                    {s.title}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--gl-muted)' }}>{s.body}</div>
                </div>
              </li>
            ))}
          </ol>

          <Callout tone="gold">
            <b>The one-sentence mental model:</b> Setup defines the facility → Docs tells you what they should have → Doc Review grades how good the docs they DO have actually are → Findings logs the hazards on the floor → Report ties it all into one deliverable + a Phase 2 quote.
          </Callout>
        </Section>
      </div>
    </>
  );
}

/* ---------- Layout primitives ---------- */

function Section({ id, eyebrow, title, children }) {
  return (
    <section id={id} className="mb-8 scroll-mt-20" data-testid={`help-section-${id}`}>
      <div
        className="text-xs uppercase tracking-widest font-bold mb-1"
        style={{ color: 'var(--gl-gold)', fontFamily: 'Inter', letterSpacing: '0.12em' }}
      >
        {eyebrow}
      </div>
      <h2
        className="font-display font-extrabold uppercase mb-3"
        style={{ color: 'var(--gl-dark)', fontSize: '1.5rem', letterSpacing: '0.02em' }}
      >
        {title}
      </h2>
      <div className="gl-card">{children}</div>
    </section>
  );
}

function SubH({ children }) {
  return (
    <div
      className="font-bold uppercase tracking-wider text-sm mt-4 mb-2"
      style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
    >
      {children}
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: '#f5f5f3' }}>
            {headers.map((h, i) => (
              <th
                key={i}
                className="text-left px-3 py-2 text-xs uppercase tracking-wider font-bold"
                style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Row({ n, tab, what, when }) {
  return (
    <tr className="border-b border-gray-100 align-top">
      <td className="px-3 py-2 font-bold" style={{ color: 'var(--gl-dark)', whiteSpace: 'nowrap' }}>{n}</td>
      <td className="px-3 py-2 font-bold" style={{ color: 'var(--gl-dark)' }}>{tab}</td>
      <td className="px-3 py-2" style={{ color: 'var(--gl-ink, #111827)' }}>{what}</td>
      <td className="px-3 py-2" style={{ color: 'var(--gl-muted)' }}>{when}</td>
    </tr>
  );
}

function Badge({ children, color, bg }) {
  return (
    <span
      className="px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider text-xs inline-block"
      style={{ background: bg, color, fontFamily: 'Inter', letterSpacing: '0.06em' }}
    >
      {children}
    </span>
  );
}

function ChipPreview({ Icon, color, label }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border-2 font-bold uppercase tracking-wider text-xs"
      style={{ background: '#fff', color, borderColor: color, fontFamily: 'Inter', letterSpacing: '0.06em' }}
    >
      <Icon size={14} weight="bold" /> {label}
    </span>
  );
}

function Callout({ children, tone = 'navy' }) {
  const styles = {
    navy: { background: '#eff4fb', border: 'var(--gl-navy)' },
    gold: { background: '#fdf6e3', border: 'var(--gl-gold)' },
    warn: { background: '#fef4ea', border: 'var(--gl-p2)' },
  }[tone];
  return (
    <div
      className="mt-4 p-3 border-l-4 rounded-sm text-sm"
      style={{ background: styles.background, borderLeftColor: styles.border, color: 'var(--gl-dark)' }}
    >
      <ShieldCheckIcon size={16} weight="fill" style={{ display: 'inline', marginRight: 6, marginBottom: -2, color: styles.border }} />
      {children}
    </div>
  );
}
